/**
 * Post-Critical Gate - LCP-First Performance Optimization
 *
 * Coordinates loading of third-party scripts and dynamic features
 * until after the critical rendering path is complete.
 *
 * Strategy: first content/load signal + idle callback, with 8s hard fallback.
 * This is a startup signal, not a final LCP measurement or an idle work queue.
 */

interface PostCriticalCallback {
  id: string;
  callback: () => void;
  priority: 'high' | 'normal' | 'low';
}

interface PostCriticalGateState {
  ready: boolean;
  lcpObserved: boolean;
  idleCallbackExecuted: boolean;
  fallbackTriggered: boolean;
  callbacks: PostCriticalCallback[];
}

type RegisteredCallback = PostCriticalCallback & { token: number };
type UnregisterPostCriticalCallback = () => void;

class PostCriticalGate {
  private state: PostCriticalGateState = {
    ready: false,
    lcpObserved: false,
    idleCallbackExecuted: false,
    fallbackTriggered: false,
    callbacks: [],
  };

  private lcpObservers: PerformanceObserver[] = [];
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private callbackToken = 0;
  private callbacksById = new Map<string, RegisteredCallback>();

  constructor() {
    if (typeof window === 'undefined') return;

    this.initializeGate();
  }

  private isLCPSupported(): boolean {
    try {
      if (!('PerformanceObserver' in window)) return false;
      const supported = (PerformanceObserver as unknown as { supportedEntryTypes?: string[] })
        .supportedEntryTypes;
      // If the browser doesn't expose the list, assume "maybe" and try anyway.
      if (!supported) return true;
      return supported.includes('largest-contentful-paint');
    } catch {
      return false;
    }
  }

  private scheduleLoadSignal(): void {
    // Document load is a fallback startup signal; later paints can still update LCP.
    // Using this as a safety net prevents the gate from waiting for an LCP signal that
    // may be unavailable or missed due to late initialization.
    try {
      if (typeof document !== 'undefined' && document.readyState === 'complete') {
        this.markLCPComplete();
        return;
      }
    } catch {
      // ignore
    }

    try {
      window.addEventListener(
        'load',
        () => {
          this.markLCPComplete();
        },
        { once: true }
      );
    } catch {
      // ignore
    }
  }

  private initializeGate(): void {
    // 1. Set up LCP observation
    this.observeLCP();
    this.scheduleLoadSignal();

    // 2. Set up idle callback
    this.scheduleIdleCallback();

    // 3. Set up 8s hard fallback
    this.scheduleFallback();
  }

  private observeLCP(): void {
    if (!this.isLCPSupported()) {
      this.markLCPComplete();
      return;
    }

    try {
      const onLCP: PerformanceObserverCallback = (entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];

        if (lastEntry && lastEntry.startTime) {
          console.debug('🎯 LCP observed:', lastEntry.startTime);
          this.markLCPComplete();
        }
      };

      // Some browsers support LCP but only via the newer `{ type, buffered }` signature.
      // Others may accept the call but not actually observe entries. To avoid a silent no-op,
      // we register both observer configurations; `markLCPComplete` is idempotent.
      const typedObserver = new PerformanceObserver(onLCP);
      this.lcpObservers.push(typedObserver);
      try {
        (typedObserver as unknown as PerformanceObserver).observe({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          type: 'largest-contentful-paint' as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          buffered: true as any,
        } as unknown as PerformanceObserverInit);
      } catch {
        // ignore; the legacy observer below may still work
      }

      const legacyObserver = new PerformanceObserver(onLCP);
      this.lcpObservers.push(legacyObserver);
      try {
        legacyObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch {
        // ignore; load fallback will still open the gate
      }
    } catch (error) {
      console.warn('LCP observer not supported:', error);
      this.markLCPComplete();
    }
  }

  private markLCPComplete(): void {
    if (this.state.lcpObserved) return;

    this.state.lcpObserved = true;
    this.checkReady();
  }

  private scheduleIdleCallback(): void {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(
        () => {
          console.debug('🕐 Idle callback executed');
          this.state.idleCallbackExecuted = true;
          this.checkReady();
        },
        { timeout: 5000 } // 5s timeout for idle callback
      );
    } else {
      // Fallback for browsers without idle callback
      setTimeout(() => {
        console.debug('🕐 Idle fallback executed');
        this.state.idleCallbackExecuted = true;
        this.checkReady();
      }, 2000);
    }
  }

  private scheduleFallback(): void {
    this.fallbackTimer = setTimeout(() => {
      console.warn('⏰ Post-critical gate fallback triggered (8s)');
      this.state.fallbackTriggered = true;
      this.checkReady();
    }, 8000);
  }

  private checkReady(): void {
    if (this.state.ready) return;

    const isReady = this.state.lcpObserved && this.state.idleCallbackExecuted;

    if (isReady || this.state.fallbackTriggered) {
      this.triggerReady();
    }
  }

  private triggerReady(): void {
    if (this.state.ready) return;

    this.state.ready = true;
    console.log('✅ Post-critical gate ready');

    // Clean up observers and timers
    this.cleanup();

    // Execute callbacks in priority order
    this.executeCallbacks();
  }

  private cleanup(): void {
    this.lcpObservers.forEach((obs) => {
      try {
        obs.disconnect();
      } catch {
        // ignore
      }
    });
    this.lcpObservers = [];

    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  private executeCallbacks(): void {
    const sortedCallbacks = [...this.callbacksById.values()].sort((a, b) => {
      const priorityOrder = { high: 0, normal: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    sortedCallbacks.forEach(({ id, callback }) => {
      try {
        console.debug(`🚀 Executing post-critical callback: ${id}`);
        callback();
      } catch (error) {
        console.error(`❌ Post-critical callback failed: ${id}`, error);
      }
    });

    // Clear callbacks after execution
    this.callbacksById.clear();
    this.syncCallbackState();
  }

  private syncCallbackState(): void {
    this.state.callbacks = [...this.callbacksById.values()].map(({ id, callback, priority }) => ({
      id,
      callback,
      priority,
    }));
  }

  /**
   * Register a callback to be executed after the critical rendering path
   *
   * @param id - Unique identifier for the callback
   * @param callback - Function to execute
   * @param priority - Execution priority (high, normal, low)
   */
  public register(
    id: string,
    callback: () => void,
    priority: 'high' | 'normal' | 'low' = 'normal'
  ): UnregisterPostCriticalCallback {
    if (this.state.ready) {
      // Gate already ready, execute immediately
      console.debug(`🚀 Executing immediate post-critical callback: ${id}`);
      try {
        callback();
      } catch (error) {
        console.error(`❌ Immediate post-critical callback failed: ${id}`, error);
      }
      return () => {};
    }

    const token = ++this.callbackToken;
    const hadExisting = this.callbacksById.has(id);
    this.callbacksById.set(id, { id, callback, priority, token });
    this.syncCallbackState();

    if (hadExisting) {
      console.debug(`♻️ Replaced post-critical callback: ${id} (${priority})`);
    } else {
      console.debug(`📝 Registered post-critical callback: ${id} (${priority})`);
    }

    // Token guard prevents stale cleanup from removing a newer callback with the same ID.
    return () => {
      const current = this.callbacksById.get(id);
      if (!current || current.token !== token) {
        return;
      }

      this.callbacksById.delete(id);
      this.syncCallbackState();
      console.debug(`🧹 Unregistered post-critical callback: ${id}`);
    };
  }

  /**
   * Check if the gate is ready (LCP settled + idle callback or fallback)
   */
  public isReady(): boolean {
    return this.state.ready;
  }

  /**
   * Get current state for debugging
   */
  public getState(): PostCriticalGateState {
    return { ...this.state };
  }

  /**
   * Force trigger the gate (for testing or emergency)
   */
  public forceReady(): void {
    console.warn('🔥 Post-critical gate force triggered');
    this.triggerReady();
  }
}

// Singleton instance
let postCriticalGateInstance: PostCriticalGate | null = null;

/**
 * Get the singleton post-critical gate instance
 */
export function getPostCriticalGate(): PostCriticalGate {
  if (!postCriticalGateInstance) {
    postCriticalGateInstance = new PostCriticalGate();
  }
  return postCriticalGateInstance;
}

/**
 * Register a post-critical callback (convenience function)
 */
export function registerPostCriticalCallback(
  id: string,
  callback: () => void,
  priority?: 'high' | 'normal' | 'low'
): UnregisterPostCriticalCallback {
  return getPostCriticalGate().register(id, callback, priority);
}

/**
 * Check if post-critical gate is ready
 */
export function isPostCriticalReady(): boolean {
  return getPostCriticalGate().isReady();
}

export type { PostCriticalCallback, PostCriticalGateState, UnregisterPostCriticalCallback };
